import React from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import {
    PRICE_LABEL,
    FULL_ANALYSIS_LABEL,
    ONE_TIME_PAYMENT_LABEL,
} from '../lib/commercialCopy';

interface Props {
    onUpgrade: () => void;
    /** Optional: server-provided contextual upgrade message */
    upgradeMessage?: string;
    /** WP1: server flag (derived.trust_estate_inclusion_unresolved) — trust property may be missing, so the report is provisional. */
    trustEstateInclusionUnresolved?: boolean;
}

const WHAT_YOU_GET = [
    'Your exact inheritance tax calculation',
    'Full breakdown of how the tax is calculated',
    'Impact of trusts, gifts, and allowances',
    'Clear guidance on what to do next',
    'A complete report you can download and keep',
];

/** WP1: the report for an unresolved trust is provisional — nothing here promises an exact or confirmed figure. */
const WHAT_YOU_GET_PROVISIONAL = [
    'Your provisional inheritance tax assessment and the information requiring review',
    'Full breakdown of the provisional calculation',
    'Impact of trusts, gifts, and allowances',
    'Clear guidance on what to do next',
    'A complete report you can download and keep',
];

/**
 * Pre-checkout pricing panel.
 *
 * Sits inline on the results page in place of the old FinalUpgradeCTA.
 * The actual checkout is handled by Stripe — this panel exists to remove
 * ambiguity about price, deliverable, and post-payment outcome BEFORE the
 * user is redirected.
 */
export const PricingPanel: React.FC<Props> = ({ onUpgrade, upgradeMessage, trustEstateInclusionUnresolved = false }) => (
    <div className="bg-white border border-[#E8E4DB]">
        <div className="px-6 pt-6 pb-5">
            {/* ── Price block ──────────────────────────────────────── */}
            <div className="text-center mb-5">
                <h2 className="font-serif text-[22px] font-normal tracking-[-0.01em] text-[#1A1A1A] m-0">
                    {FULL_ANALYSIS_LABEL} — <span className="font-mono tabular-nums text-[#3730A3]">{PRICE_LABEL}</span>
                </h2>
                <p className="text-[13px] text-[#6B6762] italic mt-1">
                    {ONE_TIME_PAYMENT_LABEL}
                </p>
            </div>

            {/* ── What you get ────────────────────────────────────── */}
            <div className="bg-[#F3EFE6] border border-[#E8E4DB] p-5 mb-4">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-3">
                    What you get
                </p>
                <div className="space-y-2">
                    {(trustEstateInclusionUnresolved === true ? WHAT_YOU_GET_PROVISIONAL : WHAT_YOU_GET).map((item, i) => (
                        <div key={i} className="flex items-baseline gap-2 text-[13px] text-[#3F3D39]">
                            <span className="font-serif text-[#3730A3] shrink-0">✓</span>
                            <span>{item}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Risk-reduction line ─────────────────────────────── */}
            <p className="text-[13px] text-[#3F3D39] leading-relaxed mb-2">
                {trustEstateInclusionUnresolved === true
                    ? 'Shows what can be calculated from the information entered, and what a trust or inheritance tax specialist needs to review.'
                    : 'Designed to give you a clear understanding of your position before speaking to a solicitor or financial adviser.'}
            </p>

            {/* Optional contextual message from backend */}
            {upgradeMessage && (
                <p className="text-[11px] text-[#6B6762] italic leading-relaxed mb-4">
                    {upgradeMessage}
                </p>
            )}

            {/* ── CTA ─────────────────────────────────────────────── */}
            <div className="flex flex-col items-center gap-2 mt-5">
                <button
                    onClick={onUpgrade}
                    className="w-full sm:w-auto bg-[#3730A3] text-white hover:bg-[#2E2780] px-5 py-3 text-[13px] font-medium tracking-[0.02em] transition-colors"
                >
                    Get full analysis — {PRICE_LABEL}
                </button>
                <p className="text-[11px] text-[#6B6762] italic flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    Instant access after payment
                </p>
            </div>

            {/* ── Trust signals ───────────────────────────────────── */}
            <div className="mt-5 pt-4 border-t border-[#E8E4DB] flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1 text-[11px] text-[#AFA99F] italic">
                <span>Based on current UK inheritance tax rules</span>
                <span className="hidden sm:inline">·</span>
                <span className="flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    Your data is private and securely handled
                </span>
            </div>
        </div>
    </div>
);
